import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField
} from "@mui/material";
import { ProgressButton, SquareIconButton, useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { getCycleCountFrequencyByVid } from "~/redux/actions/cycleCounts";
import { usePutCycleCountFrequencyMutation } from "~/redux/warehouse/cycleCountsOverhaul.hooks";

type CycleCountFrequencyModalProps = {
  open: boolean;
  initialFrequency: number | null;
  closeModalCb: () => void;
};

export function CycleCountFrequencyModal(props: CycleCountFrequencyModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const { successToast, errorToast } = useToast();
  const { initialFrequency, open, closeModalCb } = props;
  const [frequency, setFrequency] = useState<number>(1);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setFrequency(initialFrequency ?? 1);
  }, [initialFrequency, open]);

  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const workstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const [updateCycleCountFrequency] = usePutCycleCountFrequencyMutation();

  const handleAddFrequency = async (enteredFrequency: number) => {
    if (selectedVariant?.variantId) {
      try {
        await updateCycleCountFrequency({
          variantId: selectedVariant.variantId,
          frequency: enteredFrequency,
          workstationId
        }).unwrap();
        successToast(t("cycle count frequency set"));
        await dispatch(getCycleCountFrequencyByVid(selectedVariant.variantId));
      } catch (error) {
        errorToast(getMessageFromRtkError(error));
      }
      mixpanelTrack({
        trackedPageName,
        type: "Button Click",
        label: "Confirm Inventory Frequency",
        eventProperties: {
          variantId: selectedVariant.variantId,
          frequency: enteredFrequency
        }
      });

      closeModalCb();
    }
  };

  const maxFrequency = 3650; // ~10 years in days. Defined on the backend.
  const isOverMaxFrequency = frequency > maxFrequency;
  const isMinFrequency = frequency < 1;
  const isIncreaseDisabled = frequency >= maxFrequency;

  const frequencyTextField = (
    <TextField
      id="frequency"
      label="Frequency"
      type="number"
      variant="outlined"
      value={frequency}
      onChange={(e) => {
        setFrequency(Number(e.target.value));
      }}
      error={isMinFrequency || isOverMaxFrequency}
      helperText={
        isMinFrequency
          ? t("value must be greater than 0")
          : isOverMaxFrequency
            ? t("value must be less than or equal to {{max}}", {
                max: maxFrequency
              })
            : " "
      }
      inputProps={{
        min: 1,
        role: "spinbutton",
        sx: {
          fontSize: "4rem"
        }
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">{t("days")}</InputAdornment>
        )
      }}
    />
  );

  const frequencyButtons = (
    <Stack direction="column" gap={1.5} justifyContent="center" mb={2}>
      <SquareIconButton
        aria-label="increase frequency"
        onClick={() => {
          setFrequency(frequency + 1);
        }}
        size="large"
        disabled={isIncreaseDisabled}
      >
        <ExpandLessIcon sx={{ fontSize: 27 }} />
      </SquareIconButton>
      <SquareIconButton
        aria-label="decrease frequency"
        onClick={() => {
          setFrequency(frequency - 1);
        }}
        disabled={isMinFrequency || frequency <= 1}
        size="large"
      >
        <ExpandMoreIcon sx={{ fontSize: 27 }} />
      </SquareIconButton>
    </Stack>
  );

  const AddFrequencyButton = (
    <ProgressButton
      id="add-button"
      buttonSize="medium"
      emphasis="high"
      responsive
      fullWidth
      startIcon={<AddIcon style={{ fontSize: 22 }} />}
      onClick={() => handleAddFrequency(frequency)}
      disabled={isOverMaxFrequency || isMinFrequency}
      sx={{
        fontWeight: "normal"
      }}
    >
      {t("add")}
    </ProgressButton>
  );

  return (
    <Dialog
      className="CycleCountFrequency-dialog"
      onClose={() => closeModalCb()}
      open={open}
      maxWidth="xs"
    >
      <DialogTitle>{t("add cycle count frequency")}</DialogTitle>
      <DialogContent>
        <Box my={3}>
          <Stack direction="row" gap={2} alignItems="center">
            <Box flex={1} minWidth={0}>
              {frequencyTextField}
            </Box>
            {frequencyButtons}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: "column", alignItems: "stretch", px: 3, pb: 2 }}
      >
        {AddFrequencyButton}
      </DialogActions>
    </Dialog>
  );
}
